"use client";

import type { CSSProperties, RefObject } from "react";

import { formatPercentRaw } from "@/components/services/seo/seo-revenue-projection";
import { CcCalculatorHowCollapsible } from "./CcCalculatorCollapsible";

export type RoiResultImpactCopy = {
  impactEyebrow: string;
  impactHeroSubtitle: string;
  /** "{n}" replaced with formatted percent. */
  impactHeroSecondary: string;
  impactChartTitle: string;
  compareCurrentLabel: string;
  compareProjectedLabel: string;
  /** "{n}" replaced with year index (1-based). */
  yearLabel: string;
  zeroNote: string;
  incompleteNote: string;
  footnote: string;
  howLink: string;
  how: string[];
};

export type RoiResultImpactValue = {
  heroAmount: number;
  heroSecondaryPercent: number;
  callouts: { label: string; text: string }[];
  years: number[];
  compareRows: { label: string; value: number }[];
  showZeroNote: boolean;
};

type CcRoiResultImpactPanelProps = {
  copy: RoiResultImpactCopy;
  money: Intl.NumberFormat;
  result:
    | { kind: "incomplete" }
    | { kind: "value"; data: RoiResultImpactValue };
  chartRef?: RefObject<HTMLDivElement | null>;
  howOpen: boolean;
  setHowOpen: React.Dispatch<React.SetStateAction<boolean>>;
  howId: string;
};

function ProjectionBars({
  years,
  max,
  yearLabel,
  money,
}: {
  years: number[];
  max: number;
  yearLabel: string;
  money: Intl.NumberFormat;
}) {
  return (
    <ol className="cc-traffic__bars">
      {years.map((amount, index) => (
        <li key={index} className="cc-traffic__bar">
          <span className="cc-traffic__bar-track">
            <span className="cc-traffic__bar-value">{money.format(Math.round(amount))}</span>
            <span
              className="cc-traffic__bar-fill"
              aria-hidden="true"
              style={{ "--cc-bar": Math.max(0.02, amount / max) } as CSSProperties}
            />
          </span>
          <span className="cc-traffic__bar-label">
            {yearLabel.replace("{n}", String(index + 1))}
          </span>
        </li>
      ))}
    </ol>
  );
}

/** Results column shared by SEO traffic v2 and service ROI calculators. */
export function CcRoiResultImpactPanel({
  copy,
  money,
  result,
  chartRef,
  howOpen,
  setHowOpen,
  howId,
}: CcRoiResultImpactPanelProps) {
  const max =
    result.kind === "value"
      ? Math.max(1, ...result.data.years)
      : 1;

  return (
    <div
      ref={chartRef}
      className="cc-traffic__result cc-traffic__result--impact-v2"
      aria-live="polite"
    >
      <p className="cc-traffic__impact-eyebrow">{copy.impactEyebrow}</p>
      {result.kind === "value" ? (
        <>
          <div className="cc-traffic__impact-hero">
            <p className="cc-traffic__impact-hero-value">
              {money.format(Math.round(result.data.heroAmount))}
            </p>
            <p className="cc-traffic__impact-hero-sub">{copy.impactHeroSubtitle}</p>
            <p className="cc-traffic__impact-hero-secondary">
              {copy.impactHeroSecondary.replace(
                "{n}",
                formatPercentRaw(result.data.heroSecondaryPercent, money.resolvedOptions().locale),
              )}
            </p>
          </div>
          <dl className="cc-traffic__impact-stats">
            {result.data.callouts.map((item) => (
              <div key={item.label} className="cc-traffic__impact-stat">
                <dd className="cc-traffic__impact-stat-value">{item.text}</dd>
                <dt className="cc-traffic__impact-stat-label">{item.label}</dt>
              </div>
            ))}
          </dl>
          <p className="cc-traffic__chart-title cc-traffic__chart-title--impact">
            {copy.impactChartTitle}
          </p>
          <ProjectionBars
            years={result.data.years}
            max={max}
            yearLabel={copy.yearLabel}
            money={money}
          />
          {result.data.showZeroNote ? (
            <p className="cc-traffic__help">{copy.zeroNote}</p>
          ) : null}
          {result.data.compareRows.length > 0 ? (
            <div className="cc-traffic__compare">
              {result.data.compareRows.map((row) => (
                <div key={row.label} className="cc-traffic__compare-row">
                  <span className="cc-traffic__compare-label">{row.label}</span>
                  <span className="cc-traffic__compare-value">
                    {money.format(Math.round(row.value))}
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </>
      ) : (
        <p className="cc-traffic__empty">{copy.incompleteNote}</p>
      )}
      <p className="cc-traffic__footnote">{copy.footnote}</p>
      <CcCalculatorHowCollapsible
        id={howId}
        label={copy.howLink}
        open={howOpen}
        onToggle={() => setHowOpen((open) => !open)}
        lines={copy.how}
      />
    </div>
  );
}
